import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Navigation } from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Star, Package } from "lucide-react";
import { PreorderCountdown } from "../components/prodotti/PreorderCountdown";
import { NoBgImage } from "../components/prodotti/NoBgImage";
import { ViewsBadge } from "../components/prodotti/ViewsBadge";
import { trackTelegramClick } from "../utils/telegramTracking";
// WelcomeDiscountBubble import removed: bubble now mounted globally in App.js

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// HomePage Component (Sito Pubblico Completo)
export const HomePage = () => {
  const { t } = useTranslation();
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterName, setNewsletterName] = useState("");
  const [newsletterMessage, setNewsletterMessage] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [allReviews, setAllReviews] = useState([]);
  const [italyShipping, setItalyShipping] = useState({ cost: "7,90", days: "3-5 giorni", abroadFrom: "15" });
  useEffect(() => {
    axios.get(`${API}/shipping-rules`).then((r) => {
      const rules = r.data?.shipping_rules || [];
      const it = rules.find((x) => x.id === "italia" || /^ital/i.test(x.country || ""));
      const abroad = rules
        .filter((x) => x !== it && x.base_cost > 0 && x.is_active !== false && !/ital|inpost|ritiro|pickup/i.test(`${x.country || ""} ${x.id || ""} ${x.zone || ""}`))
        .map((x) => Number(x.base_cost));
      setItalyShipping({
        cost: it ? Number(it.base_cost).toFixed(2).replace(".", ",") : "7,90",
        days: (it?.delivery_days || "3-5 giorni").replace(/\s*lavorativi/i, ""),
        abroadFrom: abroad.length ? String(Math.min(...abroad)).replace(".", ",") : "15",
      });
    }).catch(() => {});
  }, []);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    customer_name: "",
    customer_email: "",
    rating: 5,
    comment: "",
    article_id: ""
  });
  const [reviewMessage, setReviewMessage] = useState("");
  const [articles, setArticles] = useState([]);
  const [promoBanners, setPromoBanners] = useState([]);
  const [activePromotionsHome, setActivePromotionsHome] = useState([]);

  // Memoized: top 8 bestsellers (available + preorder) — recomputed only when articles change
  const bestsellerArticles = useMemo(
    () => articles.filter(a => a.quantity_stock > 0 || a.availability_type === 'preorder').slice(0, 8),
    [articles]
  );

  // Memoized: Pokémon products in stock (matched by title/category)
  const pokemonArticles = useMemo(
    () => articles.filter(a => {
      const txt = `${a.title || ""} ${a.category || ""}`.toLowerCase();
      return (txt.includes("pokemon") || txt.includes("pokémon")) && (a.quantity_stock > 0 || a.availability_type === 'preorder');
    }).slice(0, 4),
    [articles]
  );

  // Load all approved reviews and articles
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch reviews (non-blocking)
        axios.get(`${API}/reviews?status=approved`)
          .then(response => {
            if (response.data && response.data.reviews) {
              setAllReviews(response.data.reviews);
            }
          })
          .catch(error => console.error("Error loading reviews:", error));
        
        // Fetch articles for dropdown (non-blocking)
        axios.get(`${API}/public/articles`)
          .then(response => {
            if (response.data && Array.isArray(response.data)) {
              setArticles(response.data);
            }
          })
          .catch(error => console.error("Error loading articles:", error));
        
        // Fetch promotional banners
        axios.get(`${API}/active-banners`)
          .then(response => {
            if (response.data && Array.isArray(response.data)) {
              setPromoBanners(response.data);
            }
          })
          .catch(error => console.error("Error loading banners:", error));

        // Fetch active promotions for product badges
        axios.get(`${API}/active-promotions`)
          .then(response => {
            if (response.data && Array.isArray(response.data)) {
              setActivePromotionsHome(response.data);
            }
          })
          .catch(error => console.error("Error loading promotions:", error));
          
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    fetchData();
  }, []);

  // Auto-scroll to reviews section and open form when URL has #recensioni
  // (?review_order=<id> from the post-delivery email pre-fills name, email and product)
  useEffect(() => {
    const orderId = new URLSearchParams(window.location.search).get('review_order');
    if (orderId) {
      axios.get(`${API}/reviews/prefill/${orderId}`)
        .then((r) => {
          const d = r.data || {};
          setReviewFormData((prev) => ({
            ...prev,
            customer_name: d.customer_name || prev.customer_name,
            customer_email: d.customer_email || prev.customer_email,
            article_id: d.items?.[0]?.id || prev.article_id,
          }));
          setShowReviewForm(true);
        })
        .catch(() => setShowReviewForm(true));
    }
    if (window.location.hash === '#recensioni' || orderId) {
      setTimeout(() => {
        const el = document.getElementById('recensioni');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          setShowReviewForm(true);
        }
      }, 800);
    }
  }, []);

  // Submit review
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    if (!reviewFormData.customer_name || !reviewFormData.customer_email || !reviewFormData.comment || !reviewFormData.article_id) {
      setReviewMessage("❌ Compila tutti i campi obbligatori");
      return;
    }

    try {
      const response = await axios.post(`${API}/reviews`, {
        article_id: reviewFormData.article_id,
        customer_name: reviewFormData.customer_name,
        customer_email: reviewFormData.customer_email,
        rating: reviewFormData.rating,
        comment: reviewFormData.comment
      });

      if (response.status === 200) {
        setReviewMessage("✅ Recensione inviata! Sarà pubblicata dopo approvazione.");
        setReviewFormData({
          customer_name: "",
          customer_email: "",
          rating: 5,
          comment: "",
          article_id: ""
        });
        setTimeout(() => {
          setShowReviewForm(false);
          setReviewMessage("");
        }, 3000);
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      setReviewMessage("❌ Errore nell'invio della recensione");
    }
  };

  // const handleNewsletterSubmit = async (e) => {
  //   e.preventDefault();
    
  //   if (!newsletterEmail.trim()) {
  //     setNewsletterMessage("Inserisci un indirizzo email valido");
  //     return;
  //   }

  //   try {
  //     setNewsletterLoading(true);
  //     setNewsletterMessage("");

  //     const response = await axios.post(`${API}/newsletter/subscribe`, {
  //       email: newsletterEmail.trim(),
  //       name: newsletterName.trim() || null
  //     });

  //     if (response.data.already_subscribed) {
  //       setNewsletterMessage("✅ Email già registrata alla newsletter!");
  //     } else {
  //       setNewsletterMessage("✅ Iscrizione completata! Controlla la tua email per la conferma e i dettagli della newsletter.");
  //       setNewsletterEmail("");
  //       setNewsletterName("");
  //     }

  //   } catch (error) {
  //     console.error("Newsletter subscription error:", error);
  //     setNewsletterMessage("❌ Errore nell'iscrizione. Riprova più tardi.");
  //   } finally {
  //     setNewsletterLoading(false);
  //   }
  // };

  const handleNewsletterSubmit = async (e) => {
  e.preventDefault();
  
  if (!newsletterEmail.trim()) {
    setNewsletterMessage("Inserisci un indirizzo email valido");
    return;
  }

  try {
    setNewsletterLoading(true);
    setNewsletterMessage("");

    const response = await axios.post(`${API}/newsletter/subscribe`, {
      email: newsletterEmail.trim(),
      name: newsletterName.trim() || null
    });

    // Batch state updates using React 18's automatic batching
    // Add small delay to ensure DOM is ready
    await new Promise(resolve => setTimeout(resolve, 0));

    if (response.data.already_subscribed) {
      setNewsletterMessage("✅ Email già registrata alla newsletter!");
    } else {
      setNewsletterMessage("✅ Iscrizione completata! Controlla la tua email per la conferma e i dettagli della newsletter.");
      // Batch these together
      setNewsletterEmail("");
      setNewsletterName("");
    }

  } catch (error) {
    console.error("Newsletter subscription error:", error);
    setNewsletterMessage("❌ Errore nell'iscrizione. Riprova più tardi.");
  } finally {
    setNewsletterLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Dynamic <head> tags (React 19 hoists into <head> natively) */}
      <title>Edicola Cerasuolo — Modellismo F1 Ferrari, Pokemon TCG, Centauria | Villaricca (NA)</title>
      <meta name="description" content="Edicola Cerasuolo: modellini F1 Ferrari LookSmart 1:43, carte Pokemon TCG, Racing Car Transporters Centauria, Truck Tuning Collection. Spedizioni internazionali assicurate, pagamenti sicuri Stripe, Klarna." />
      <link rel="canonical" href="https://edicolavillaricca.com/" />
      {/* Per-page OpenGraph + Twitter Card (was previously in index.html, moved here
          to avoid shadowing product-page og:* tags). */}
      <meta property="og:title" content="Edicola Cerasuolo — Modellismo F1, Pokemon TCG, Centauria" />
      <meta property="og:description" content="Modellini F1 Ferrari 1:43, carte Pokemon TCG, Centauria Racing Transporters e Truck Tuning. Spedizioni in tutta Europa." />
      <meta property="og:url" content="https://edicolavillaricca.com/" />
      <meta property="og:type" content="website" />
      <meta name="twitter:title" content="Edicola Cerasuolo — Modellismo F1, Pokemon TCG, Centauria" />
      <meta name="twitter:description" content="Modellini F1 Ferrari 1:43, carte Pokemon TCG, Centauria Racing Transporters e Truck Tuning." />
      {/* Schema.org WebSite with SiteLinks SearchBox */}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Edicola Cerasuolo",
        "url": "https://edicolavillaricca.com",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://edicolavillaricca.com/prodotti?search={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      })}</script>

      <Navigation />

      {/* WelcomeDiscountBubble now mounted globally in App.js so it appears
          on all pages (not just HomePage). Removed from here to avoid double
          rendering. */}
      
      {/* Promo Banner */}
      {promoBanners.length > 0 && (
        <div className="bg-gradient-to-r from-pink-600 to-fuchsia-500 py-2 px-4">
          <div className="max-w-7xl mx-auto text-center">
            {promoBanners.map((b) => (
              <p key={b.code} className="text-sm md:text-base font-bold text-white tracking-wide" data-testid="promo-banner">
                {b.banner_text} — Codice: <span className="bg-white/30 px-2 py-0.5 rounded font-mono">{b.code}</span>
              </p>
            ))}
          </div>
        </div>
      )}

      {/* HERO — split stile "Parisi": testo a sx, prodotti in evidenza a dx */}
      <header className="relative overflow-hidden bg-[#0a0a0f]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-pink-900/10 via-transparent to-[#0a0a0f]"></div>
          <div className="absolute top-0 left-1/3 w-[700px] h-[350px] bg-pink-600/5 blur-[120px] rounded-full"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 pt-12 pb-14 md:pt-16 md:pb-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Colonna testo */}
          <div>
            <p className="text-pink-500 text-xs font-bold tracking-[0.3em] uppercase mb-4 flex items-center gap-2">
              <span className="inline-block w-8 h-[2px] bg-pink-500"></span> EDICOLA CERASUOLO · VILLARICCA
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[1.05] mb-5">
              <span className="text-white">Il punto di </span>
              <span className="text-pink-500">riferimento</span><br />
              <span className="text-white text-3xl sm:text-4xl lg:text-5xl">per modellismo e collezionismo</span><br />
              <span className="text-fuchsia-400">a Villaricca.</span>
            </h1>
            <p className="text-gray-400 text-base mb-7 max-w-lg">
              Esperti in <span className="text-white font-semibold">modellismo</span> — Centauria, DeAgostini, Hachette, LookSmart —
              carte <span className="text-white font-semibold">Pokémon TCG</span> e pezzi da collezione. Spedizioni in Italia e in tutto il mondo.
            </p>
            <div className="flex flex-wrap gap-3 mb-7">
              <a href="/prodotti" className="inline-flex items-center px-7 py-3 bg-pink-600 hover:bg-pink-500 text-white font-extrabold uppercase tracking-wide rounded-sm skew-x-[-6deg] shadow-lg shadow-pink-600/25 transition-colors" data-testid="hero-cta-catalog">
                <span className="skew-x-[6deg]">Modellismo →</span>
              </a>
              <a href="#pezzi-top" className="inline-flex items-center px-7 py-3 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-extrabold uppercase tracking-wide rounded-sm skew-x-[-6deg] shadow-lg shadow-fuchsia-500/25 transition-colors" data-testid="hero-cta-top">
                <span className="skew-x-[6deg]">Pezzi Top →</span>
              </a>
            </div>
            <div className="flex flex-wrap gap-5 text-xs text-gray-400 uppercase tracking-wider">
              <span className="flex items-center gap-1.5"><span className="text-pink-500">🛡</span> Stripe</span>
              <span className="flex items-center gap-1.5"><span className="text-pink-500">🚚</span> Spedizioni internazionali</span>
              <span className="flex items-center gap-1.5"><span className="text-pink-500">✔</span> Originali</span>
            </div>
          </div>
          {/* Colonna prodotti in evidenza */}
          <div className="grid grid-cols-2 gap-4" data-testid="hero-featured">
            {bestsellerArticles.slice(0, 2).map((article, i) => (
              <a key={article.id} href={`/prodotto/${article.slug || article.id}`} className="group relative bg-[#12121a] border border-gray-800 hover:border-pink-500/60 rounded-lg overflow-hidden transition-colors">
                <span className="absolute top-2 left-2 z-10 bg-pink-600 text-white text-[10px] font-extrabold uppercase px-2 py-1">{i === 0 ? "Nuovo Arrivo" : "Novità"}</span>
                <div className="aspect-[3/4] bg-[#0d0d14] overflow-hidden">
                  <NoBgImage article={article} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <ViewsBadge articleId={article.id} className="top-2 right-2" />
                <div className="p-3 bg-gradient-to-t from-black to-[#12121a]">
                  <p className="text-white text-xs font-bold uppercase line-clamp-2">{article.title}</p>
                  <p className="text-pink-400 font-extrabold text-sm mt-1">{(article.price * (1 + parseFloat(article.iva_percentage || 0) / 100)).toFixed(2)} €</p>
                </div>
              </a>
            ))}
            <div className="col-span-2 bg-gradient-to-r from-pink-600 to-fuchsia-500 rounded-lg px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-white/90 text-[10px] font-bold uppercase tracking-widest">⚡ Spediz.</p>
                <p className="text-white font-extrabold text-2xl leading-none" data-testid="home-shipping-italy-cost">€{italyShipping.cost}</p>
              </div>
              <p className="text-white/90 text-xs font-bold uppercase text-right" data-testid="home-shipping-italy-days">Italia · {italyShipping.days}<br />Estero da €{italyShipping.abroadFrom}</p>
            </div>
          </div>
        </div>
        {/* separatore a scacchi */}
        <div className="h-3 w-full" style={{ backgroundImage: "repeating-conic-gradient(#fff 0% 25%, #0a0a0f 0% 50%)", backgroundSize: "12px 12px" }}></div>
      </header>

      {/* ARRIVI DELLA SETTIMANA — 2 card grandi */}
      {bestsellerArticles.length > 0 && (
        <section className="py-14 bg-[#0d0d14]" id="arrivi">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-extrabold uppercase mb-8 flex items-center gap-3">
              <span className="inline-block w-10 h-[3px] bg-pink-500"></span>
              <span className="text-white">Arrivi</span> <span className="text-pink-500">della settimana</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="weekly-arrivals">
              {(bestsellerArticles.length >= 4 ? bestsellerArticles.slice(2, 4) : bestsellerArticles.slice(0, 2)).map((article) => {
                const priceWithIva = article.price * (1 + parseFloat(article.iva_percentage || 0) / 100);
                return (
                  <div key={article.id} className="group relative bg-[#12121a] border border-gray-800 hover:border-pink-500/60 rounded-lg overflow-hidden transition-colors" data-testid={`weekly-arrival-${article.id}`}>
                    <span className="absolute top-3 left-3 z-10 bg-pink-600 text-white text-xs font-extrabold uppercase px-3 py-1">Arrivo della settimana</span>
                    <a href={`/prodotto/${article.slug || article.id}`} className="block aspect-square bg-[#0d0d14] overflow-hidden">
                      <NoBgImage article={article} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </a>
                    <ViewsBadge articleId={article.id} className="top-3 right-3" />
                    <div className="p-5 flex items-end justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-pink-500 text-[10px] font-bold uppercase tracking-widest mb-1">Nuova uscita</p>
                        <h3 className="text-white font-extrabold uppercase text-lg leading-tight line-clamp-2">{article.title}</h3>
                        <p className="text-fuchsia-400 font-extrabold text-2xl mt-2">€{priceWithIva.toFixed(2)}</p>
                      </div>
                      <a href={`/prodotto/${article.slug || article.id}`} className="shrink-0 inline-flex items-center px-5 py-2.5 bg-pink-600 hover:bg-pink-500 text-white font-extrabold uppercase text-xs tracking-wide skew-x-[-6deg] transition-colors">
                        <span className="skew-x-[6deg]">Acquista →</span>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* TRUST STRIP — 4 box */}
      <section className="py-8 bg-[#0a0a0f] border-y border-gray-900">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: "❤", top: "Oltre", strong: "1000+ clienti soddisfatti" },
            { icon: "🌍", top: "Spedizione", strong: "In tutto il mondo" },
            { icon: "🏪", top: "Ritiro gratuito", strong: "In edicola" },
            { icon: "🔒", top: "Pagamenti", strong: "100% sicuri" },
          ].map((b) => (
            <div key={b.strong} className="border border-gray-800 rounded-md px-4 py-3 flex items-center gap-3 bg-[#0d0d14]">
              <span className="text-pink-500 text-xl">{b.icon}</span>
              <div>
                <p className="text-gray-500 text-[10px] uppercase tracking-wider">{b.top}</p>
                <p className="text-white text-xs font-extrabold uppercase">{b.strong}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIE — Grandi e cliccabili */}
      <section className="py-14 bg-[#0d0d14]">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-center text-2xl font-extrabold uppercase text-white mb-8">{t('home.explore_collections')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="home-categories">
            {[
              { name: "Truck Tuning", filter: "/collezione/truck-tuning", img: "/images/categories/truck.png", desc: "Modelli Esclusivi" },
              { name: "Pokemon TCG", filter: "/collezione/pokemon-tcg", img: "/images/categories/pokemon.png", desc: "Carte e Set" },
              { name: "Auto da Corsa", filter: "/collezione/auto-da-corsa-f1", img: "/images/categories/f1.png", desc: "F1 e Rally" },
              { name: "Racing Car", filter: "/collezione/racing-car-transporters", img: "/images/categories/transporter.png", desc: "Transporters" },
            ].map(cat => (
              <a key={cat.name} href={cat.filter} data-testid={`category-card-${cat.filter.split('/').pop()}`}
                className="group bg-[#12121a] border border-gray-800 rounded-xl p-5 text-center hover:border-pink-500/60 hover:bg-[#16161f] transition-all duration-300">
                <div className="h-32 md:h-36 flex items-center justify-center mb-3">
                  <img src={cat.img} alt={cat.name} loading="lazy" className="max-h-full max-w-full object-contain drop-shadow-[0_10px_20px_rgba(236,72,153,0.15)] group-hover:scale-110 transition-transform duration-500" />
                </div>
                <span className="text-white font-extrabold uppercase block text-base">{cat.name}</span>
                <span className="text-gray-500 text-xs mt-1 block">{cat.desc}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* PEZZI TOP — I Piu Venduti */}
      {articles.length > 0 && (
        <section className="py-16 bg-[#0a0a0f]" id="pezzi-top">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-pink-500 text-[11px] font-bold uppercase tracking-[0.3em] mb-1">In pole position</p>
                <h2 className="text-3xl md:text-4xl font-extrabold uppercase"><span className="text-white">Pezzi</span> <span className="text-pink-500">Top</span></h2>
              </div>
              <a href="/prodotti" className="text-pink-400 hover:text-pink-300 text-xs font-bold uppercase tracking-wider">Vedi tutti →</a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {bestsellerArticles.map((article, idx) => {
                  const priceWithIva = article.price * (1 + parseFloat(article.iva_percentage || 0) / 100);
                  const promo = activePromotionsHome.find(p => p.applicable_articles.length === 0 || p.applicable_articles.includes(article.id));
                  const discounted = promo ? (promo.discount_type === "percentage" ? priceWithIva * (1 - promo.discount_value / 100) : Math.max(0, priceWithIva - promo.discount_value)) : null;
                  return (
                    <a key={article.id} href={`/prodotto/${article.slug || article.id}`} target="_blank" rel="noopener noreferrer" className="group bg-[#12121a] border border-gray-800 rounded-xl overflow-hidden hover:border-pink-500/30 transition-all duration-300" data-testid={`bestseller-${article.id}`}>
                      {/* Badges */}
                      <div className="relative">
                        {(article.images_url?.[0] || article.photo_url) ? (
                          <div className="aspect-square overflow-hidden bg-[#0d0d14]">
                            <NoBgImage article={article} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          </div>
                        ) : (
                          <div className="aspect-square bg-[#0d0d14] flex items-center justify-center text-gray-700 text-5xl">📦</div>
                        )}
                        <ViewsBadge articleId={article.id} className="top-2 right-2" />
                        <div className="absolute top-2 left-2 flex flex-col gap-1">
                          {idx < 3 && <span className="bg-pink-500 text-black text-[10px] font-bold px-2 py-0.5 rounded">{t('home.bestseller_badge')}</span>}
                          {promo && <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">-{promo.discount_value}%</span>}
                          {article.availability_type === 'preorder' && <span className="bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">{t('home.preorder_badge')}</span>}
                        </div>
                        {article.quantity_stock > 0 && article.quantity_stock <= 5 && (
                          <div className="absolute bottom-2 right-2">
                            <span className="bg-red-600/90 text-white text-[10px] font-bold px-2 py-0.5 rounded">{t('home.last_pieces')} {article.quantity_stock} {t('home.pieces')}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="text-white text-sm font-semibold line-clamp-2 mb-2 group-hover:text-pink-400 transition-colors">{article.title}</h3>
                        {article.preorder_date && (
                          <div className="mb-2">
                            <p className="text-blue-400 text-xs">{t('home.available_from')}: {article.preorder_date}</p>
                            {article.availability_type === 'preorder' && <PreorderCountdown preorderDate={article.preorder_date} variant="dark" />}
                          </div>
                        )}
                        <div className="flex items-end justify-between">
                          <div>
                            {discounted ? (
                              <>
                                <span className="text-gray-500 line-through text-xs">{priceWithIva.toFixed(2)} €</span>
                                <span className="text-pink-400 font-bold text-lg ml-1">{discounted.toFixed(2)} €</span>
                              </>
                            ) : (
                              <span className="text-pink-400 font-bold text-lg">{priceWithIva.toFixed(2)} €</span>
                            )}
                          </div>
                          {article.availability_type === 'preorder' ? (
                            <span className="text-blue-400 text-[10px] font-semibold">{t('home.preorder_now')}</span>
                          ) : article.quantity_stock > 0 ? (
                            <span className="text-green-500 text-[10px] font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>{t('home.available')}</span>
                          ) : null}
                        </div>
                      </div>
                    </a>
                  );
                })}
            </div>
            <div className="text-center mt-10">
              <a href="/prodotti" className="inline-flex items-center px-8 py-3 bg-pink-500 text-black font-bold rounded-lg hover:bg-pink-400 transition-all duration-200 shadow-lg shadow-pink-500/20">
                {t('home.view_all_catalog')}
              </a>
            </div>
          </div>
        </section>
      )}

      {/* POKÉMON IN STOCK */}
      <section className="py-14 bg-[#0d0d14] border-y border-yellow-500/10" id="pokemon" data-testid="pokemon-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-yellow-400 text-[11px] font-bold uppercase tracking-[0.3em] mb-1">⚡ Gotta catch 'em all</p>
              <h2 className="text-3xl md:text-4xl font-extrabold uppercase"><span className="text-white">Pokémon</span> <span className="text-yellow-400">in stock</span></h2>
            </div>
            <a href="/collezione/pokemon-tcg" className="text-yellow-400 hover:text-yellow-300 text-xs font-bold uppercase tracking-wider" data-testid="pokemon-view-all">Vedi tutti →</a>
          </div>
          {pokemonArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pokemon-products">
              {pokemonArticles.map((article) => {
                const priceWithIva = article.price * (1 + parseFloat(article.iva_percentage || 0) / 100);
                return (
                  <a key={article.id} href={`/prodotto/${article.slug || article.id}`} className="group bg-[#12121a] border border-gray-800 rounded-xl overflow-hidden hover:border-yellow-400/50 transition-all duration-300" data-testid={`pokemon-product-${article.id}`}>
                    <div className="relative">
                      <div className="aspect-square overflow-hidden bg-[#0a0a0f]">
                        <NoBgImage article={article} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                      <span className="absolute top-2 left-2 bg-yellow-400 text-black text-[10px] font-extrabold uppercase px-2 py-0.5 rounded">Pokémon TCG</span>
                      <ViewsBadge articleId={article.id} className="top-2 right-2" />
                      {article.quantity_stock > 0 && article.quantity_stock <= 5 && (
                        <span className="absolute bottom-2 right-2 bg-red-600/90 text-white text-[10px] font-bold px-2 py-0.5 rounded">Ultimi {article.quantity_stock} pz</span>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-white text-sm font-semibold line-clamp-2 mb-2 group-hover:text-yellow-300 transition-colors">{article.title}</h3>
                      <div className="flex items-end justify-between">
                        <span className="text-yellow-400 font-bold text-lg">{priceWithIva.toFixed(2)} €</span>
                        {article.quantity_stock > 0 && (
                          <span className="text-green-500 text-[10px] font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>{t('home.available')}</span>
                        )}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <a href="/collezione/pokemon-tcg" className="group flex flex-col md:flex-row items-center gap-6 bg-gradient-to-r from-[#12121a] via-[#16161f] to-yellow-500/5 border border-yellow-500/20 hover:border-yellow-400/50 rounded-xl p-6 md:p-8 transition-all duration-300" data-testid="pokemon-empty-banner">
              <img src="/images/categories/pokemon.png" alt="Pokemon TCG" loading="lazy" className="h-36 md:h-44 object-contain drop-shadow-[0_10px_25px_rgba(250,204,21,0.15)] group-hover:scale-105 transition-transform duration-500" />
              <div className="text-center md:text-left">
                <h3 className="text-white font-extrabold uppercase text-xl md:text-2xl mb-2">Carte, buste e set <span className="text-yellow-400">Pokémon TCG</span></h3>
                <p className="text-gray-400 text-sm mb-4 max-w-md">Nuovi arrivi in continuo aggiornamento: booster pack, Elite Trainer Box e carte singole. Scopri la collezione completa.</p>
                <span className="inline-flex items-center px-6 py-2.5 bg-yellow-400 group-hover:bg-yellow-300 text-black font-extrabold uppercase text-xs tracking-wide skew-x-[-6deg] transition-colors">
                  <span className="skew-x-[6deg]">Esplora Pokémon →</span>
                </span>
              </div>
            </a>
          )}
        </div>
      </section>

      {/* RECENSIONI IN EVIDENZA — le migliori, subito dopo Pokémon */}
      {allReviews.length > 0 && (
        <section className="py-12 bg-[#0a0a0f]" data-testid="featured-reviews">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-end justify-between mb-7">
              <div>
                <p className="text-pink-500 text-[11px] font-bold uppercase tracking-[0.3em] mb-1">Parlano di noi</p>
                <h2 className="text-2xl md:text-3xl font-extrabold uppercase"><span className="text-white">Recensioni</span> <span className="text-pink-500">in evidenza</span></h2>
              </div>
              <a href="#recensioni" className="text-pink-400 hover:text-pink-300 text-xs font-bold uppercase tracking-wider" data-testid="featured-reviews-all">Tutte le recensioni →</a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[...allReviews].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 3).map(review => (
                <div key={review.id} className="relative bg-[#12121a] border border-gray-800 rounded-xl p-6 hover:border-pink-500/30 transition-all duration-300" data-testid={`featured-review-${review.id}`}>
                  <span className="absolute -top-3 left-5 bg-pink-600 text-white text-[10px] font-extrabold uppercase px-2.5 py-1 rounded">★ Top</span>
                  <div className="flex items-center gap-0.5 mb-3 mt-1">
                    {[1,2,3,4,5].map(s => (<span key={s} className={`text-xl ${s <= review.rating ? 'text-yellow-400' : 'text-gray-700'}`}>★</span>))}
                  </div>
                  <p className="text-gray-300 text-sm mb-4 line-clamp-4 italic">"{review.comment}"</p>
                  <p className="text-pink-400 text-xs font-bold uppercase">— {review.customer_name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OFFERTA SETTIMANALE */}
      <section className="py-12 bg-gradient-to-r from-pink-600/10 via-[#0d0d14] to-pink-600/10 border-y border-pink-500/10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-pink-400 text-xs font-semibold tracking-[0.2em] uppercase mb-2">{t('home.limited_offer')}</p>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">{t('home.weekly_promo')}</h3>
          <p className="text-gray-400 mb-6">{t('home.weekly_promo_desc')}</p>
          <a href="/prodotti" className="inline-flex items-center px-6 py-3 border-2 border-pink-500 text-pink-400 font-bold rounded-lg hover:bg-pink-500 hover:text-black transition-all duration-200">
            {t('home.discover_offer')}
          </a>
        </div>
      </section>

      {/* SERVIZI / TRUST */}
      <section className="py-16 bg-[#0d0d14]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-[#12121a] border border-gray-800 rounded-xl p-7 text-center hover:border-green-500/30 transition-all duration-300">
              <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
              </div>
              <h3 className="text-white font-bold mb-2">{t('home.insured_shipping')}</h3>
              <p className="text-gray-500 text-sm">{t('home.insured_shipping_desc')}</p>
            </div>
            <div className="bg-[#12121a] border border-gray-800 rounded-xl p-7 text-center hover:border-pink-500/30 transition-all duration-300">
              <div className="w-12 h-12 bg-pink-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>
              </div>
              <h3 className="text-white font-bold mb-2">{t('home.pay_installments')}</h3>
              <p className="text-gray-500 text-sm">{t('home.pay_installments_desc')}</p>
            </div>
            <div className="bg-[#12121a] border border-gray-800 rounded-xl p-7 text-center hover:border-blue-500/30 transition-all duration-300">
              <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
              </div>
              <h3 className="text-white font-bold mb-2">{t('home.secure_payments')}</h3>
              <p className="text-gray-500 text-sm">{t('home.secure_payments_desc')}</p>
            </div>
          </div>
          {/* Payment logos (PayPal removed 28 May 2026 — account closed) */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            {["Visa", "Mastercard", "Klarna", "Satispay", "Bonifico"].map(m => (
              <span key={m} className="text-gray-600 text-xs font-medium bg-[#12121a] border border-gray-800 px-3 py-1.5 rounded">{m}</span>
            ))}
          </div>
        </div>
      </section>

      {/* MARKETPLACE — Ci trovi anche su */}
      <section className="py-14 bg-[#0a0a0f] border-t border-gray-900" data-testid="marketplaces-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8">
            <p className="text-pink-500 text-[11px] font-bold uppercase tracking-[0.3em] mb-1">Multi-channel</p>
            <h2 className="text-3xl md:text-4xl font-extrabold uppercase"><span className="text-white">Ci trovi</span> <span className="text-pink-500">anche su</span></h2>
            <p className="text-gray-500 text-sm mt-2">Stessi prodotti originali, più canali per acquistare in sicurezza.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a href="https://ebay.it/str/edicolacerasuolo" target="_blank" rel="noopener noreferrer" data-testid="marketplace-ebay"
              className="group relative overflow-hidden bg-[#12121a] border border-gray-800 hover:border-yellow-400/60 rounded-xl p-8 transition-all duration-300">
              <div className="absolute -right-16 -top-16 w-56 h-56 bg-gradient-to-br from-[#E53238] via-[#FFCE00] to-[#0064D2] opacity-15 blur-3xl group-hover:opacity-30 transition-opacity duration-500"></div>
              <div className="relative flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-extrabold uppercase tracking-wider text-[#FFCE00]">eBay</span>
                  <span className="ml-auto text-xs uppercase tracking-widest text-gray-500 px-3 py-1.5 border border-gray-700 rounded group-hover:border-yellow-400/60 group-hover:text-yellow-300 transition-colors">Apri lo store →</span>
                </div>
                <div className="text-sm font-mono text-gray-400">ebay.it/str/edicolacerasuolo</div>
                <p className="text-gray-500 text-sm">Aste e Compralo Subito: modellini, camion d'epoca e collezioni anche sul nostro store eBay ufficiale.</p>
              </div>
            </a>
            <a href="https://it.wallapop.com/app/catalog/published" target="_blank" rel="noopener noreferrer" data-testid="marketplace-wallapop"
              className="group relative overflow-hidden bg-[#12121a] border border-gray-800 hover:border-[#13C1AC]/60 rounded-xl p-8 transition-all duration-300">
              <div className="absolute -right-16 -top-16 w-56 h-56 bg-gradient-to-br from-[#13C1AC] to-[#0a8a7a] opacity-15 blur-3xl group-hover:opacity-30 transition-opacity duration-500"></div>
              <div className="relative flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-extrabold uppercase tracking-wider text-[#13C1AC]">Wallapop</span>
                  <span className="ml-auto text-xs uppercase tracking-widest text-gray-500 px-3 py-1.5 border border-gray-700 rounded group-hover:border-[#13C1AC]/60 group-hover:text-[#13C1AC] transition-colors">Apri →</span>
                </div>
                <div className="text-sm font-mono text-gray-400">Edicola Cerasuolo</div>
                <p className="text-gray-500 text-sm">Annunci e occasioni anche su Wallapop: spedizioni protette in tutta Italia.</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* COMMUNITY WHATSAPP */}
      <section className="relative py-16 bg-[#0d0d14] border-y border-[#25D366]/15 overflow-hidden" data-testid="whatsapp-community-section">
        <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#25D366]/10 blur-3xl rounded-full"></div>
        <div className="max-w-6xl mx-auto px-4 relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[#25D366] text-[11px] font-bold uppercase tracking-[0.3em] mb-2">💬 Community WhatsApp</p>
            <h2 className="text-3xl md:text-4xl font-extrabold uppercase leading-tight">
              <span className="text-white">Resta in contatto</span><br />
              <span className="text-[#25D366]">su WhatsApp</span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-md text-sm leading-relaxed">
              Anteprime delle nuove uscite <span className="text-white font-semibold">Centauria, Hachette, DeAgostini</span>,
              disponibilità <span className="text-white font-semibold">Pokémon TCG</span> e assistenza diretta.
              Scrivici: rispondiamo velocemente. <span className="text-white font-semibold">Gratis.</span>
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://wa.me/393470808245?text=Ciao!%20Vorrei%20info%20e%20anteprime%20sul%20modellismo%20(Truck%20Tuning%2C%20Centauria%2C%20F1)" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-white text-black font-extrabold uppercase text-xs tracking-wide skew-x-[-6deg] transition-colors" data-testid="whatsapp-modellismo-cta">
                <span className="skew-x-[6deg]">🏁 Modellismo →</span>
              </a>
              <a href="https://wa.me/393470808245?text=Ciao!%20Vorrei%20info%20e%20novit%C3%A0%20sulle%20carte%20Pok%C3%A9mon%20TCG" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 hover:bg-white text-black font-extrabold uppercase text-xs tracking-wide skew-x-[-6deg] transition-colors" data-testid="whatsapp-pokemon-cta">
                <span className="skew-x-[6deg]">⚡ Pokémon TCG →</span>
              </a>
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-widest text-gray-600">· Gratis · Senza obbligo di acquisto ·</p>
          </div>
          <div className="space-y-4">
            {[
              { icon: "🔔", title: "Anteprime uscite", desc: "Scopri prima di tutti le nuove uscite Centauria, Hachette, DeAgostini." },
              { icon: "📦", title: "Disponibilità in tempo reale", desc: "Chiedi se un pezzo è arrivato o prenota le prossime uscite." },
              { icon: "🤝", title: "Assistenza diretta", desc: "Ordini, spedizioni e consigli: parli direttamente con noi." },
            ].map((b) => (
              <div key={b.title} className="flex items-start gap-4 bg-[#12121a] border border-gray-800 hover:border-[#25D366]/50 rounded-xl p-5 transition-colors">
                <div className="shrink-0 w-11 h-11 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg flex items-center justify-center text-xl">{b.icon}</div>
                <div>
                  <h3 className="text-white font-extrabold uppercase text-sm">{b.title}</h3>
                  <p className="text-gray-500 text-xs mt-1 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-amber-400/20 bg-[#11111a] py-14" data-testid="advertising-home-section">
        <div className="mx-auto flex max-w-6xl flex-col justify-between gap-8 px-4 md:flex-row md:items-end"><div className="max-w-2xl"><p className="text-xs font-bold uppercase tracking-wide text-amber-400">Per le imprese del territorio</p><h2 className="mt-3 text-3xl font-bold text-white">Hai un’attività commerciale?</h2><p className="mt-3 leading-7 text-gray-400">Promuovi la tua azienda, i tuoi prodotti e i tuoi servizi attraverso gli spazi pubblicitari di Edicola Villaricca.</p></div><a href="/pubblicita" className="shrink-0 border border-amber-400 bg-amber-400 px-5 py-3 text-sm font-bold text-black transition-colors hover:bg-transparent hover:text-amber-300" data-testid="advertising-home-cta">Scopri le opportunità pubblicitarie</a></div>
      </section>

      {/* RECENSIONI */}
      <section id="recensioni" className="py-16 bg-[#0a0a0f]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('home.customer_reviews')}</h3>
            <p className="text-gray-500 text-sm mb-6">{t('home.verified_reviews')}</p>
            <Button onClick={() => setShowReviewForm(!showReviewForm)} className={showReviewForm ? "bg-gray-700 hover:bg-gray-600" : "bg-pink-500 hover:bg-pink-400 text-black font-bold"} data-testid="toggle-review-form">
              {showReviewForm ? t('home.close_form') : t('home.leave_review')}
            </Button>
          </div>
          
          {showReviewForm && (
            <div className="max-w-lg mx-auto mb-12 bg-[#12121a] border border-gray-800 rounded-xl p-6">
              <form onSubmit={handleReviewSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">{t('home.name')} *</label>
                    <input type="text" value={reviewFormData.customer_name} onChange={(e) => setReviewFormData({...reviewFormData, customer_name: e.target.value})} className="w-full px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">{t('home.email')} *</label>
                    <input type="email" value={reviewFormData.customer_email} onChange={(e) => setReviewFormData({...reviewFormData, customer_email: e.target.value})} className="w-full px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">{t('home.product')} *</label>
                  <select value={reviewFormData.article_id} onChange={(e) => setReviewFormData({...reviewFormData, article_id: e.target.value})} className="w-full px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent" required>
                    <option value="">{t('home.select_product')}</option>
                    {articles.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">{t('home.rating')}</label>
                  <div className="flex gap-1">{[1,2,3,4,5].map(s => (<button key={s} type="button" onClick={() => setReviewFormData({...reviewFormData, rating: s})} className={`text-2xl ${s <= reviewFormData.rating ? 'text-pink-400' : 'text-gray-700'}`}>★</button>))}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">{t('home.comment')} *</label>
                  <textarea rows="3" value={reviewFormData.comment} onChange={(e) => setReviewFormData({...reviewFormData, comment: e.target.value})} className="w-full px-3 py-2 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent" required />
                </div>
                {reviewMessage && <p className={`text-sm ${reviewMessage.includes('✅') ? 'text-green-400' : 'text-red-400'}`}>{reviewMessage}</p>}
                <Button type="submit" className="w-full bg-pink-500 hover:bg-pink-400 text-black font-bold">{t('home.submit_review')}</Button>
              </form>
            </div>
          )}

          {allReviews.length === 0 ? (
            <p className="text-center text-gray-600 py-8">{t('home.no_reviews')}</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
                {(showAllReviews ? allReviews : allReviews.slice(0, 6)).map(review => (
                  <div key={review.id} className="bg-[#12121a] border border-gray-800 rounded-xl p-5 hover:border-pink-500/20 transition-all duration-300" data-testid={`review-card-${review.id}`}>
                    <div className="flex items-center gap-0.5 mb-3">{[1,2,3,4,5].map(s => (<span key={s} className={`text-lg ${s <= review.rating ? 'text-pink-400' : 'text-gray-700'}`}>★</span>))}</div>
                    <p className={`text-gray-300 text-sm mb-3 italic ${showAllReviews ? '' : 'line-clamp-3'}`}>"{review.comment}"</p>
                    <p className="text-pink-400/70 text-xs font-semibold">— {review.customer_name}</p>
                  </div>
                ))}
              </div>
              {allReviews.length > 6 && (
                <div className="text-center mt-8">
                  <button
                    type="button"
                    onClick={() => setShowAllReviews(v => !v)}
                    className="border border-pink-500/60 text-pink-400 hover:bg-pink-500 hover:text-black font-bold text-sm px-6 py-2.5 rounded-full transition-colors"
                    data-testid="reviews-toggle-all"
                  >
                    {showAllReviews ? 'Mostra meno' : `Mostra tutte le ${allReviews.length} recensioni`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-14 bg-[#0d0d14] border-t border-gray-800">
        <div className="max-w-xl mx-auto px-4 text-center">
          <h3 className="text-xl font-bold text-white mb-2">{t('home.stay_updated')}</h3>
          <p className="text-gray-500 text-sm mb-6">{t('home.newsletter_desc')}</p>
          <form onSubmit={handleNewsletterSubmit} className="space-y-3">
            <input type="email" value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} placeholder={t('home.your_email')} className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent" required data-testid="newsletter-email-input" />
            <input type="text" value={newsletterName} onChange={(e) => setNewsletterName(e.target.value)} placeholder={t('home.your_name_opt')} className="w-full px-4 py-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent" data-testid="newsletter-name-input" />
            <Button type="submit" disabled={newsletterLoading} className="w-full bg-pink-500 hover:bg-pink-400 text-black font-bold py-3" data-testid="newsletter-submit-btn">
              {newsletterLoading ? t('home.sending') : t('home.subscribe_now')}
            </Button>
            {newsletterMessage && <p className={`text-sm ${newsletterMessage.includes('✅') ? 'text-green-400' : 'text-red-400'}`}>{newsletterMessage}</p>}
          </form>
        </div>
      </section>

      {/* Social */}
      <section className="py-10 bg-[#0a0a0f] border-t border-gray-800" id="contatti">
        <div className="max-w-xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm mb-4">{t('home.follow_social')}</p>
          <div className="flex justify-center gap-4">
            <a href="https://t.me/edicolavillaricca" target="_blank" rel="noopener noreferrer" onClick={() => trackTelegramClick("home_social")} data-testid="home-telegram-link" className="w-10 h-10 bg-sky-500/10 hover:bg-sky-500/20 rounded-full flex items-center justify-center transition-all"><svg className="w-5 h-5 text-sky-400" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg></a>
            <a href="https://wa.me/393470808245" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-green-500/10 hover:bg-green-500/20 rounded-full flex items-center justify-center transition-all"><svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/></svg></a>
            <a href="https://facebook.com/edicolavillaricca" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-500/10 hover:bg-blue-500/20 rounded-full flex items-center justify-center transition-all"><svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg></a>
            <a href="https://instagram.com/edicolavillaricca" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-pink-500/10 hover:bg-pink-500/20 rounded-full flex items-center justify-center transition-all"><svg className="w-5 h-5 text-pink-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg></a>
            <a href="https://tiktok.com/@edicolavillaricca" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-500/10 hover:bg-gray-500/20 rounded-full flex items-center justify-center transition-all"><svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg></a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#08080d] border-t border-gray-800 text-white py-10" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-sm">
            <div>
              <h3 className="font-bold mb-3 text-pink-400">Edicola Cerasuolo</h3>
              <p className="text-gray-500">Specializzati in modellismo F1 e collezioni Pokemon a Villaricca (NA)</p>
            </div>
            <div>
              <h3 className="font-bold mb-3">Contatti</h3>
              <address className="text-gray-500 not-italic space-y-1">
                <p>📍 Corso Europa 175, Villaricca (NA)</p>
                <p>📞 <a href="tel:+393470808245" className="hover:text-pink-400">+39 347 080 8245</a></p>
                <p>📧 <a href="mailto:amm@edicolavillaricca.com" className="hover:text-pink-400">amm@edicolavillaricca.com</a></p>
              </address>
            </div>
            <div>
              <h3 className="font-bold mb-3">Vendita</h3>
              <ul className="text-gray-500 space-y-1">
                <li><a href="https://ebay.it/str/edicolacerasuolo" target="_blank" rel="noopener noreferrer" className="hover:text-pink-400">eBay</a></li>
                <li><a href="https://it.wallapop.com/app/catalog/published" target="_blank" rel="noopener noreferrer" className="hover:text-pink-400">Wallapop</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-3">Link</h3>
              <ul className="text-gray-500 space-y-1">
                <li><a href="/" className="hover:text-pink-400">Home</a></li>
                <li><a href="/prodotti" className="hover:text-pink-400">Prodotti</a></li>
                <li><a href="/chi-siamo" className="hover:text-pink-400">Chi Siamo</a></li>
                <li><a href="/privacy-policy" className="hover:text-pink-400">Privacy & Cookie</a></li>
                <li><a href="/pubblicita" className="hover:text-pink-400" data-testid="footer-advertising-link">Pubblicità e partnership</a></li>
                <li><a href="/termini" className="hover:text-pink-400">Termini di Vendita</a></li>
                <li><a href="/resi" className="hover:text-pink-400">Norme sui Resi</a></li>
                <li>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event("openCookiePreferences"))}
                    className="hover:text-pink-400 text-left"
                    data-testid="footer-cookie-preferences"
                  >
                    Gestisci preferenze cookie
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-gray-600 text-xs">© {new Date().getFullYear()} Edicola Cerasuolo — Tutti i diritti riservati</p>
            <a href="https://www.emergentagent.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-400 text-xs">Made with Emergent</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
